import { deepEqual, parseUser, userEqual } from '../utils.js';
import { createEventEmitter } from './eventEmitter.js';
function reducer(prevState, action) {
    switch (action.type) {
        case 'login_request':
            return {
                status: 'logging-in',
                user: undefined,
            };
        case 'login_success':
        case 'storage_sync': {
            const newUser = parseUser(action.accessTokenData);
            // Don't update the state if the user data hasn't changed.
            // This can happen if we were already logged in in another tab.
            if (prevState.user && userEqual(newUser, prevState.user)) {
                return prevState;
            }
            return {
                status: 'logged-in',
                user: parseUser(action.accessTokenData),
            };
        }
        case 'login_error':
            return {
                status: 'logged-out',
                loginError: action.error,
                user: undefined,
            };
        case 'refresh_success': {
            const newUser = parseUser(action.accessTokenData);
            // Don't update the state if user data hasn't changed between refreshes
            if (prevState.user && userEqual(newUser, prevState.user)) {
                return prevState;
            }
            return {
                status: 'logged-in',
                user: newUser,
            };
        }
        case 'refresh_failure': {
            if (prevState.status !== 'logged-in') {
                throw new Error('@creditornot/wolt-auth ended up in impossible state');
            }
            return {
                status: 'logged-in',
                authCorrupted: true,
                user: prevState.user,
            };
        }
        case 'logout':
            return {
                status: 'logged-out',
                user: undefined,
            };
        default:
            throw new Error();
    }
}
export function createStore({ initialAccessTokenData, storage, logger }) {
    const initialState = initialAccessTokenData
        ? { status: 'logged-in', user: parseUser(initialAccessTokenData) }
        : { status: 'logged-out', user: undefined };
    const { trigger, ...eventEmitterActions } = createEventEmitter({
        initialValues: {
            state: initialState,
            status: initialState.status,
            user: initialState.user,
            accessTokenData: initialAccessTokenData,
        },
    });
    /** A non-persistent state that lives in memory */
    let state = initialState;
    /** A Redux/useReducer-like dispatch that handles both state changes and async effects (writes to the persistent storage and notifications) */
    const dispatch = async (action) => {
        // Update the state
        state = reducer(state, action);
        // Update the storage with the incoming access token data or clear it in case of logout
        if (action.type === 'logout') {
            await storage.clear();
        }
        else if (action.type !== 'storage_sync' && 'accessTokenData' in action) {
            await storage.save(action.accessTokenData);
        }
        // Read the access token data again from the storage to notify listeners if it's changed
        const accessTokenData = await storage.read();
        // Trigger the update events to notify listeners
        trigger('state', state);
        trigger('status', state.status);
        trigger('user', state.user);
        trigger('accessTokenData', accessTokenData, deepEqual);
        logger.logAction(action.type, state);
    };
    logger.logAction('initialize', state);
    return {
        getState() {
            return state;
        },
        dispatch,
        ...eventEmitterActions,
    };
}
